import { useEffect, useState } from "react";
import {
  api,
  type ControlPlaneSummary,
  type ControlPlaneSupervisorView,
  type MissionDetailResponse,
  type MissionListItemView,
} from "../api";
import { queueStateClass } from "./queueStateStyling";
import { statusLabel } from "../domain/statusCopy";
import { HumanGateInbox } from "./HumanGateInbox";
import type { ControlPlaneAreaId } from "./ownerNavigation";

/**
 * OWNEXP Slice 2 — Overview als One-Glance-Zitatseite (DNA 02 DC-21, 11 R-IA-1,
 * R-VH-1..5). Die Primärzone beantwortet oben im ersten Viewport: Autonomie,
 * aktuelle Arbeit, nächste automatische Aktion, Owner-Bedarf, Queue-Zustand,
 * letzter Fehler, letzter Abschluss. Alles wird aus den kanonischen Read-Models
 * zitiert (Summary + Supervisor), nichts selbst gerechnet (IA-C1) — und es gibt
 * genau EIN Aktualisieren (IA-C3, das der Fläche, nicht des Panels).
 */
export function ControlPlaneOverviewPanel({
  onOpenMission,
  onOpenArea,
  refreshKey = 0,
}: {
  onOpenMission?: (missionId: string) => void;
  onOpenArea?: (area: ControlPlaneAreaId) => void;
  refreshKey?: number;
} = {}): JSX.Element | null {
  const [summary, setSummary] = useState<ControlPlaneSummary | null>(null);
  const [supervisor, setSupervisor] = useState<ControlPlaneSupervisorView | null>(null);
  const [missions, setMissions] = useState<MissionListItemView[]>([]);
  const [missionDetails, setMissionDetails] = useState<Record<string, MissionDetailResponse>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = (): void => {
    setLoading(true);
    setError(null);
    // OWNEXP F-01-Fix (Claude): Die Primärzone hängt NIE mehr an /api/missions
    // (20-s-Proxy-Abbruch machte sie blind). Aktive Missionen kommen kanonisch
    // aus summary.semantics.missions.activeList — dieselbe Quelle wie
    // Missions & Runs. Detail-Anreicherung läuft fehlertolerant nach.
    Promise.all([api.controlPlaneSummary(), api.controlPlaneSupervisor().catch(() => null)])
      .then(([s, sup]) => {
        setSummary(s);
        setSupervisor(sup);
        const liste = (s.semantics?.missions?.activeList ?? []) as unknown as MissionListItemView[];
        setMissions(liste);
        return Promise.all(
          liste.slice(0, 10).map((mission) =>
            api.missionDetail(mission.id).then(
              (d) => [mission.id, d] as const,
              () => [mission.id, null] as const,
            ),
          ),
        );
      })
      .then((details) => {
        const map: Record<string, MissionDetailResponse | null> = {};
        for (const [id, d] of details) map[id] = d;
        setMissionDetails(map as Record<string, MissionDetailResponse>);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  // IA-C3: genau EIN Aktualisieren pro Seite — der der Fläche. Das Panel lädt
  // bei Refresh-Key-Wechsel nach (kein eigener Button mehr).
  useEffect(() => {
    if (refreshKey > 0) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const aktiveMitItems = missions.filter((m) => missionDetails[m.id]);
  const semantics = summary?.semantics ?? null;
  const supervisorGesund = supervisor?.health?.executorRunning === true;
  const nextEligible = supervisor?.nextEligible?.[0] ?? null;
  // recentErrors (technische Form) und recentTransitions (Event-Form) werden
  // auf eine Anzeigeform normalisiert — DC-33: Fehler immer mit Kontext.
  const letzterFehler =
    supervisor?.recentTransitions?.find((e) => e.eventType.includes("fail") || e.eventType.includes("cancel")) ??
    (supervisor?.recentErrors?.[0]
      ? {
          eventType: supervisor.recentErrors[0].kind,
          occurredAt: supervisor.recentErrors[0].at,
          title: supervisor.recentErrors[0].detail,
        }
      : null);
  const letzterAbschluss =
    supervisor?.recentTransitions?.find(
      (e) => e.eventType.includes("complete") || e.eventType.includes("done"),
    ) ?? null;
  // S0.4c single counter truth: gates.open ist DIE Primaer-Entscheidungs-
  // Zahl (Summe missionIds ueber Batch-Bundles) — dieselbe Wahrheit wie der
  // TopBar-Header (ownerGate.openCount). null = WARMING (unbekannt, nicht 0).
  const ownerBedarf = summary?.gates?.open ?? null;

  return (
    <div className="space-y-4" aria-label="Control Plane Overview" data-testid="control-plane-overview">
      {error && (
        <div className="card p-4 text-rose-300 text-sm" role="alert">
          Control-Plane-Status nicht abrufbar: {error}
        </div>
      )}

      {/* ── EMBER_CALM §3/§4: 1) LÄUFT GERADE — immer primär, volle Breite, mit
           sanfter Aktivitätsanzeige (Reduced Motion respektiert via CSS) ── */}
      <section className="card p-4 sm:p-5" data-testid="overview-current-work" aria-label="Läuft gerade">
        <div className="flex items-center gap-2">
          {semantics !== null && semantics.runs.activeList.length > 0 && (
            <span className="simo-live-dot" aria-hidden="true" data-testid="overview-live-indicator" />
          )}
          <span className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Läuft gerade</span>
        </div>
        {semantics === null ? (
          <p className="mt-2 text-amber-300 text-sm">RUN READ MODEL UNAVAILABLE.</p>
        ) : semantics.runs.activeList.length === 0 ? (
          <p className="mt-2 text-sm text-jarvis-muted">
            {supervisorGesund
              ? "Gerade kein Lauf aktiv — SIMO prüft alle paar Sekunden selbst weiter."
              : "SIMO wartet — gerade kein Lauf aktiv."}{" "}
            {semantics.leases.active > 0 ? `${semantics.leases.active} Lease(s) gehalten.` : ""}
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {semantics.runs.activeList.slice(0, 3).map((run, index) => {
              const r = run as Record<string, unknown>;
              const titel = typeof r.title === "string" ? r.title : typeof r.runId === "string" ? r.runId : "Lauf";
              const status = typeof r.status === "string" ? r.status : null;
              return (
                <li key={index} className="min-w-0">
                  <div className="truncate text-sm font-medium" title={titel}>{titel}</div>
                  <div className="text-xs text-jarvis-muted">
                    {statusLabel(status) ?? (status ? `Status: ${status}` : "Status nicht gemessen")}
                  </div>
                </li>
              );
            })}
            {semantics.leases.active > 0 && (
              <li className="text-[11px] text-jarvis-muted">
                {semantics.leases.active} aktive Writer-Lease(s) — Zuordnung und Ablauf in der Queue.
              </li>
            )}
          </ul>
        )}
        {summary !== null && supervisor !== null && (
          <p className="mt-2 text-[11px] text-jarvis-muted">
            {supervisor.lastTick?.finishedAt
              ? `Letzter Prüftakt: ${new Date(supervisor.lastTick.finishedAt).toLocaleTimeString("de-DE")} · alle ${Math.round((supervisor.cadence?.intervalMs ?? 5000) / 1000)} s`
              : "Taktzeitpunkt nicht abrufbar"}
            {supervisor.lastTick && supervisor.lastTick.admitted > 0 ? ` · ${supervisor.lastTick.admitted} gestartet` : ""}
          </p>
        )}
      </section>

      {/* ── 2) BRAUCHT DEINE ENTSCHEIDUNG + 3) NÄCHSTE SCHRITTE ── */}
      <div className="grid gap-3 md:grid-cols-2" data-testid="control-plane-primary-zone">
        <div className={`card p-4 ${ownerBedarf ? "border-amber-500/40" : ""}`} data-testid="overview-owner-action">
          <span className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Braucht SIMO dich?</span>
          {ownerBedarf === null ? (
            <p className="mt-2 text-amber-300 text-sm">UNAVAILABLE — Gates nicht abrufbar.</p>
          ) : ownerBedarf > 0 ? (
            <>
              <strong className="block text-lg text-amber-300">Ja — {ownerBedarf} Entscheidung{ownerBedarf === 1 ? "" : "en"}</strong>
              <button
                type="button"
                className="btn mt-2 min-h-11 text-xs"
                onClick={() => onOpenArea?.("gates")}
              >
                Entscheidung öffnen
              </button>
            </>
          ) : (
            <>
              <strong className="block text-lg">Nein — nichts erforderlich</strong>
              <p className="mt-1 text-xs text-jarvis-muted">Keine offenen Entscheidungen. SIMO arbeitet allein weiter.</p>
            </>
          )}
        </div>

        <div className="card p-4" data-testid="overview-next-action">
          <span className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Nächste Schritte</span>
          {supervisor === null ? (
            <p className="mt-2 text-amber-300 text-sm">SUPERVISOR READ MODEL UNAVAILABLE.</p>
          ) : nextEligible === null ? (
            <p className="mt-2 text-sm text-jarvis-muted">
              {supervisor.lastTick
                ? "Im letzten Takt war kein Item startbereit — SIMO prüft weiter (alle paar Sekunden)."
                : "Noch kein Takt ausgewertet."}
            </p>
          ) : (
            <>
              <div className="mt-1 truncate text-sm font-medium" title={nextEligible.title}>
                {nextEligible.title}
              </div>
              <p className="mt-1 text-xs text-jarvis-muted">
                {nextEligible.naechsteBedingung}
                {nextEligible.warumWartend ? ` — ${nextEligible.warumWartend}` : ""}
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── 4) WARTEN / FEHLER / HINWEISE — einklappbar (progressive disclosure,
           EMBER_CALM: technische Statistik nicht im primären Blickfeld) ── */}
      <details className="card p-4" data-testid="overview-secondary-zone">
        <summary className="cursor-pointer select-none text-sm font-medium text-jarvis-muted">
          Warten, Fehler &amp; Systemhinweise{" "}
          {letzterFehler ? <span className="text-rose-300">· letzter Fehler vorhanden</span> : ""}
          {summary && summary.queue.states.length > 0 ? ` · ${summary.queue.states.reduce((n, s) => n + s.count, 0)} Items` : ""}
        </summary>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div data-testid="overview-queue-health">
            <span className="text-[10px] uppercase tracking-wider text-jarvis-muted">Warteschlange</span>
            {summary === null ? (
              <p className="mt-2 text-amber-300 text-sm">UNAVAILABLE.</p>
            ) : summary.queue.states.length === 0 ? (
              <p className="mt-2 text-sm text-jarvis-muted">Keine verwalteten Items.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {summary.queue.states.map((s) => (
                  <span
                    key={s.state}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${queueStateClass(s.state)}`}
                    title={`${s.state}: ${statusLabel(s.state) ?? "kein Registereintrag"}`}
                  >
                    {statusLabel(s.state) ?? s.state}
                    <span className="font-mono">{s.count}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div data-testid="overview-recent-outcome">
            <span className="text-[10px] uppercase tracking-wider text-jarvis-muted">Letzter Fehler / Abschluss</span>
            {letzterFehler === null && letzterAbschluss === null ? (
              <p className="mt-2 text-sm text-jarvis-muted">Noch keine Ereignisse.</p>
            ) : (
              <div className="mt-2 space-y-2 text-xs">
                {letzterFehler && (
                  <div className="min-w-0">
                    <span className="text-rose-300">Fehler:</span>{" "}
                    <span className="align-middle" title={`${letzterFehler.eventType} ${letzterFehler.occurredAt}`}>
                      {letzterFehler.title ?? letzterFehler.eventType}
                    </span>{" "}
                    <span className="text-jarvis-muted">
                      {new Date(letzterFehler.occurredAt).toLocaleTimeString("de-DE")}
                    </span>
                  </div>
                )}
                {letzterAbschluss && (
                  <div className="min-w-0">
                    <span className="text-emerald-300">Abgeschlossen:</span>{" "}
                    <span title={`${letzterAbschluss.eventType} ${letzterAbschluss.occurredAt}`}>
                      {letzterAbschluss.title ?? letzterAbschluss.eventType}
                    </span>{" "}
                    <span className="text-jarvis-muted">
                      {new Date(letzterAbschluss.occurredAt).toLocaleTimeString("de-DE")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          {summary && (
            <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="overview-system">
              <SystemPunkt
                label="Datenbank"
                ok={summary.system.dbOk}
                okText="verbunden"
                failText="nicht erreichbar"
              />
              <SystemPunkt
                label="Executor"
                ok={summary.system.supervisor.executor?.configured === true}
                okText={
                  summary.system.supervisor.executor?.running
                    ? "aktiv"
                    : "konfiguriert (läuft nicht)"
                }
                failText="nicht konfiguriert"
              />
              <div>
                <div className="text-[10px] text-jarvis-muted uppercase tracking-wider">Offene Gates</div>
                <div className={`text-lg font-semibold ${(ownerBedarf ?? 0) > 0 ? "text-amber-300" : ""}`}>
                  {ownerBedarf ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-jarvis-muted uppercase tracking-wider">Offene Findings</div>
                <div className={`text-lg font-semibold ${summary.findings.open > 0 ? "text-amber-300" : ""}`}>
                  {summary.findings.open}
                </div>
              </div>
              <div className="md:col-span-4 text-[11px] text-jarvis-muted">
                Stand: {new Date(summary.generatedAt).toLocaleString("de-DE")} · Scopes {summary.scopes.verified} verifiziert /{" "}
                {summary.scopes.failClosed} fail-closed
                {summary.queue.scopeUnverified > 0 ? ` · ${summary.queue.scopeUnverified} Items warten auf Scope-Verifikation` : ""}
              </div>
            </div>
          )}
        </div>
      </details>

      {/* ── Aktive Missionen: kanonische Quelle semantics.missions (D-2-Fix) ── */}
      <div className="card p-3 sm:p-4">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
          Aktive Missionen
        </h3>
        {semantics !== null && semantics.missions.active === 0 ? (
          <div className="text-sm text-jarvis-muted">
            Keine aktiven Missionen in der kanonischen Queue-Sicht.
          </div>
        ) : aktiveMitItems.length === 0 ? (
          <div className="text-sm text-jarvis-muted">
            {summary === null
              ? "Zusammenfassung nicht abrufbar — keine Ersatzwerte."
              : `${semantics?.missions.active ?? missions.length} aktive Mission(en) — Details im Bereich Missions & Runs.`}
          </div>
        ) : (
          <div className="space-y-3">
            {aktiveMitItems.map((m) => {
              const detail = missionDetails[m.id]!;
              const brauchtOwner = detail.items.some(
                (it) => it.queue_state === "WAIT_OWNER" || it.queue_state === "WAIT_HUMAN",
              );
              return (
                <div
                  key={m.id}
                  className="border-b border-jarvis-border last:border-0 pb-3 last:pb-0"
                  data-testid={`control-plane-mission-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate" title={`${m.goal} (${m.id})`}>
                        {String((m as { title?: string }).title ?? (m.goal.length > 120 ? `${m.goal.slice(0, 117)}…` : m.goal))}
                      </div>
                    </div>
                    {brauchtOwner && (
                      <span className="shrink-0 rounded-full border border-rose-500/40 bg-rose-500/20 px-2.5 py-1 text-xs font-medium text-rose-300">
                        Owner-Aktion nötig
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {detail.items.slice(0, 8).map((it) => (
                      <span
                        key={it.id}
                        className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${queueStateClass(it.queue_state ?? "")}`}
                        title={`${it.title} (${it.id})${it.queue_state_reason ? ` — ${it.queue_state_reason}` : ""}`}
                      >
                        <span className="truncate max-w-[14rem]">{it.title}</span>
                        <span className="shrink-0">{statusLabel(it.queue_state ?? "") ?? it.queue_state ?? "—"}</span>
                      </span>
                    ))}
                    {detail.items.length > 8 && (
                      <span className="text-[11px] text-jarvis-muted">+{detail.items.length - 8} weitere</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* S0 — "Braucht dich": offene PRIMAERE Owner-Gates (AUTHORITY_ADVANCE,
          S0.4c: Batch-Bundle-Entscheidungen — GLEICHE Zahl/Quelle wie der
          TopBar-Header) als primaerer Handlungshinweis mit Sprung zur
          Entscheidungsflaeche. Historische Singles erscheinen hier NIE. */}
      {(ownerBedarf ?? 0) > 0 && (
        <button
          className="w-full rounded border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-left text-sm text-amber-200 hover:bg-amber-400/20"
          onClick={() => onOpenArea?.("gates")}
          data-testid="control-plane-braucht-dich"
        >
          <strong>Braucht dich:</strong> {ownerBedarf} offene Freigabe
          {ownerBedarf === 1 ? "" : "n"} — Entscheidung treffen
          <span className="block text-xs text-amber-200/70">Zur Entscheidung (Human Gates)</span>
        </button>
      )}

      {/* CP-12 — Human Gate Inbox (erste Klasse, mit Resolve-Aktionen) */}
      <HumanGateInbox onChanged={load} />

    </div>
  );
}

function SystemPunkt({
  label,
  ok,
  okText,
  failText,
}: {
  label: string;
  ok: boolean;
  okText: string;
  failText: string;
}): JSX.Element {
  return (
    <div>
      <div className="text-[10px] text-jarvis-muted uppercase tracking-wider">{label}</div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className={"inline-block w-2 h-2 rounded-full " + (ok ? "bg-emerald-400" : "bg-rose-400")} />
        <span className={"text-sm font-medium " + (ok ? "" : "text-rose-300")}>{ok ? okText : failText}</span>
      </div>
    </div>
  );
}
